import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import AdminHeader from '../../../components/feature/AdminHeader';

interface PartnerChurch {
  id: string;
  name: string;
  location: string;
  type: string;
  icon: string;
  image_url: string;
  description: string;
  display_order: number;
  created_at: string;
}

const ICON_OPTIONS = [
  { value: 'church', label: '교회', icon: 'ri-community-line' },
  { value: 'global', label: '글로벌', icon: 'ri-global-line' },
  { value: 'map', label: '지도', icon: 'ri-map-pin-line' },
  { value: 'team', label: '팀', icon: 'ri-team-line' },
  { value: 'hands', label: '협력', icon: 'ri-hand-heart-line' },
  { value: 'building', label: '건물', icon: 'ri-building-line' },
  { value: 'home', label: '홈', icon: 'ri-home-heart-line' },
  { value: 'flag', label: '깃발', icon: 'ri-flag-line' },
  { value: 'compass', label: '나침반', icon: 'ri-compass-line' },
  { value: 'earth', label: '지구', icon: 'ri-earth-line' }
];

const TYPE_OPTIONS = [
  { value: 'church', label: '협력교회' },
  { value: 'mission', label: '협력선교지' },
  { value: 'organization', label: '협력기관' }
];

export default function PartnerChurchesAdmin() {
  const [partners, setPartners] = useState<PartnerChurch[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    type: 'church',
    icon: 'church',
    image_url: '',
    description: '',
    display_order: 0
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const { data, error } = await supabase
        .from('partner_churches')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setPartners(data || []);
    } catch (error) {
      console.error('Error fetching partners:', error);
      alert('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드 가능합니다.');
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `partner-churches/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      setFormData(prev => ({ ...prev, image_url: publicUrl }));
      alert('이미지가 업로드되었습니다.');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.location || !formData.image_url || !formData.description) {
      alert('모든 필드를 입력해주세요.');
      return;
    }

    try {
      if (editingId) {
        const { error } = await supabase
          .from('partner_churches')
          .update({
            ...formData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingId);

        if (error) throw error;
        alert('수정되었습니다.');
        setEditingId(null);
      } else {
        const { error } = await supabase
          .from('partner_churches')
          .insert([formData]);

        if (error) throw error;
        alert('추가되었습니다.');
      }

      setFormData({
        name: '',
        location: '',
        type: 'church',
        icon: 'church',
        image_url: '',
        description: '',
        display_order: 0
      });
      fetchPartners();
    } catch (error) {
      console.error('Error saving partner:', error);
      alert('저장에 실패했습니다.');
    }
  };

  const handleEdit = (partner: PartnerChurch) => {
    setFormData({
      name: partner.name,
      location: partner.location,
      type: partner.type,
      icon: partner.icon,
      image_url: partner.image_url,
      description: partner.description,
      display_order: partner.display_order
    });
    setEditingId(partner.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      const { error } = await supabase
        .from('partner_churches')
        .delete()
        .eq('id', id);

      if (error) throw error;
      alert('삭제되었습니다.');
      fetchPartners();
    } catch (error) {
      console.error('Error deleting partner:', error);
      alert('삭제에 실패했습니다.');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({
      name: '',
      location: '',
      type: 'church',
      icon: 'church',
      image_url: '',
      description: '',
      display_order: 0
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader title="협력교회 관리" subtitle="협력처 정보를 관리합니다" />

      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        {/* Form */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">
            {editingId ? '협력처 수정' : '협력처 추가'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  협력처 이름 *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="예: 서울중앙교회"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  지역 *
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="예: 서울 강남구"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  유형 *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  {TYPE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  아이콘 *
                </label>
                <select
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  {ICON_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                  <span>선택된 아이콘:</span>
                  <i className={`${ICON_OPTIONS.find(o => o.value === formData.icon)?.icon} text-2xl text-teal-600`}></i>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  표시 순서
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="0"
                />
                <p className="mt-1 text-xs text-gray-500">숫자가 작을수록 먼저 표시됩니다</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  대표 이미지 *
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
                {uploading && <p className="mt-2 text-sm text-teal-600">업로드 중...</p>}
                {formData.image_url && (
                  <div className="mt-2">
                    <img src={formData.image_url} alt="Preview" className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-lg" />
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                설명 *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-sm"
                placeholder="협력처에 대한 설명을 입력하세요"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={uploading}
                className="w-full sm:w-auto px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:bg-gray-400 whitespace-nowrap cursor-pointer"
              >
                {editingId ? '수정하기' : '추가하기'}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="w-full sm:w-auto px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  취소
                </button>
              )}
            </div>
          </form>
        </div>

        {/* List */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">등록된 협력처 목록</h2>
          {partners.length === 0 ? (
            <p className="text-gray-500 text-center py-8">등록된 협력처가 없습니다.</p>
          ) : (
            <div className="space-y-4">
              {partners.map((partner) => (
                <div key={partner.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <img
                      src={partner.image_url}
                      alt={partner.name}
                      className="w-full sm:w-24 h-40 sm:h-24 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <i className={`${ICON_OPTIONS.find(o => o.value === partner.icon)?.icon} text-xl sm:text-2xl text-teal-600`}></i>
                            <h3 className="text-base sm:text-lg font-bold text-gray-900">{partner.name}</h3>
                            <span className="px-2 py-1 bg-teal-100 text-teal-700 text-xs rounded-full whitespace-nowrap">
                              {TYPE_OPTIONS.find(t => t.value === partner.type)?.label}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">
                            <i className="ri-map-pin-line mr-1"></i>
                            {partner.location}
                          </p>
                          <p className="text-sm text-gray-700 line-clamp-2">{partner.description}</p>
                        </div>
                        <div className="flex gap-2 self-end sm:self-start flex-shrink-0">
                          <button
                            onClick={() => handleEdit(partner)}
                            className="flex-1 sm:flex-none px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                          >
                            수정
                          </button>
                          <button
                            onClick={() => handleDelete(partner.id)}
                            className="flex-1 sm:flex-none px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                          >
                            삭제
                          </button>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        순서: {partner.display_order} | 등록일: {new Date(partner.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
